import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import AdminPageLayout from "../../../components/midpoint/admin/layout/AdminPageLayout";
import AdminPageHeader from "../../../components/midpoint/admin/layout/AdminPageHeader";
import { AdminAutomotiveNav } from "../automotive/AdminAutomotiveNav";
import {
  CITY_STATUS_LABELS,
  LAUNCH_STAGE_LABELS,
  READINESS_LABELS,
} from "../../../constants/automapCityExpansionConstants";
import {
  completeCityChecklistItem,
  fetchCityCampaigns,
  fetchCityDealerPipeline,
  fetchCityExpansionAnalytics,
  fetchCityInventoryReadiness,
  fetchCityPlacements,
  fetchCityReadiness,
  fetchCitySeoReadiness,
  fetchExpansionCity,
  generateCityCampaigns,
  generateCitySeoConfigs,
  trackCityExpansionEvent,
} from "../../../services/automapCityExpansionApi";
import { AdminExpansionNav, CityPageShell } from "./AdminExpansionShared";

function CityLayout({ title, active, children }) {
  const { citySlug } = useParams();
  return (
    <AdminPageLayout>
      <AdminPageHeader eyebrow="AutoMapa · Expansión" title={title} description={`Ciudad: ${citySlug}`} />
      <AdminAutomotiveNav active="expansion" />
      <AdminExpansionNav active="cities" />
      <main className="admin-console__main wrap">
        <CityPageShell citySlug={citySlug} active={active}>{children}</CityPageShell>
      </main>
    </AdminPageLayout>
  );
}

export function AdminExpansionCityDetailPage() {
  const { citySlug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    fetchExpansionCity(citySlug)
      .then(setData)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [citySlug]);

  useEffect(() => {
    load();
  }, [load]);

  const config = data?.config;
  const metrics = data?.metrics;
  const readiness = data?.readiness;

  return (
    <CityLayout title={config?.city || "Ciudad"} active="detail">
      {loading ? <p className="dealer-state-box">Cargando…</p> : null}
      {error ? <p className="dealer-state-box">{error}</p> : null}
      {config ? (
        <>
          <section className="dealer-state-box">
            <p>{CITY_STATUS_LABELS[config.status]} · {LAUNCH_STAGE_LABELS[config.launchStage] || config.launchStage} · Prioridad {config.priority}</p>
            <div className="dashboard-lead-stats">
              <span>Dealers: {metrics?.activeDealers}/{config.targetDealerCount}</span>
              <span>Verificados: {metrics?.verifiedDealers}/{config.targetVerifiedDealerCount}</span>
              <span>Listings: {metrics?.activeListings}/{config.targetActiveListingCount}</span>
              <span>Clean: {metrics?.cleanListings}/{config.targetCleanListingCount}</span>
              <span>Freshness: {metrics?.freshnessPercent}%</span>
              <span>Readiness: {readiness?.score} · {READINESS_LABELS[readiness?.label] || readiness?.label}</span>
            </div>
          </section>
          <section className="dealer-state-box">
            <h3>Acciones recomendadas</h3>
            <ul>{(data.recommendedActions || []).map((a) => <li key={a.id}><Link to={a.href}>{a.message}</Link></li>)}</ul>
          </section>
          <div className="import-actions">
            <Link className="admin-btn" to={`/admin/expansion/cities/${citySlug}/readiness`}>Readiness</Link>
            <Link className="admin-btn" to={`/admin/expansion/cities/${citySlug}/dealers`}>Dealers</Link>
            <Link className="admin-btn" to={`/admin/expansion/cities/${citySlug}/seo`}>SEO</Link>
          </div>
        </>
      ) : null}
    </CityLayout>
  );
}

export function AdminExpansionCityReadinessPage() {
  const { citySlug } = useParams();
  const [data, setData] = useState(null);

  useEffect(() => {
    fetchCityReadiness(citySlug).then(setData).catch(() => {});
    trackCityExpansionEvent("city_readiness_viewed", { citySlug });
  }, [citySlug]);

  const checklist = data?.checklist;

  return (
    <CityLayout title="Launch readiness" active="readiness">
      {data ? (
        <>
          <div className="admin-dashboard__metrics-grid">
            <div className="admin-dashboard__metric-card"><span>Score</span><strong>{data.readiness?.score}/100</strong></div>
            <div className="admin-dashboard__metric-card"><span>Label</span><strong>{READINESS_LABELS[data.readiness?.label]}</strong></div>
            <div className="admin-dashboard__metric-card"><span>Soft launch</span><strong>{data.readiness?.softLaunchReady ? "Sí" : "No"}</strong></div>
            <div className="admin-dashboard__metric-card"><span>Full launch</span><strong>{data.readiness?.fullLaunchReady ? "Sí" : "No"}</strong></div>
          </div>
          {data.readiness?.blockers?.length ? (
            <p className="dealer-state-box">Blockers: {data.readiness.blockers.join(", ")}</p>
          ) : null}
          <ul>
            {(checklist?.checklistItems || []).map((item) => (
              <li key={item.key}>
                <label>
                  <input
                    type="checkbox"
                    checked={item.completed}
                    onChange={() => completeCityChecklistItem(citySlug, item.key).then(() => fetchCityReadiness(citySlug).then(setData))}
                  />
                  {item.label}
                </label>
              </li>
            ))}
          </ul>
        </>
      ) : (
        <p className="dealer-state-box">Cargando…</p>
      )}
    </CityLayout>
  );
}

export function AdminExpansionCityDealersPage() {
  const { citySlug } = useParams();
  const [rows, setRows] = useState([]);

  useEffect(() => {
    fetchCityDealerPipeline(citySlug)
      .then((payload) => setRows(payload.rows || []))
      .catch(() => setRows([]));
    trackCityExpansionEvent("city_dealer_pipeline_viewed", { citySlug });
  }, [citySlug]);

  return (
    <CityLayout title="Dealer pipeline" active="dealers">
      <div className="import-table-wrap">
        <table className="admin-table">
          <thead>
            <tr><th>Nombre</th><th>Stage</th><th>Verificación</th><th>Listings</th><th>Calidad</th><th /></tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                <td>{row.name}</td>
                <td>{row.stage}</td>
                <td>{row.verificationStatus}</td>
                <td>{row.activeListings}</td>
                <td>{row.qualityScore ?? "—"}</td>
                <td>{row.dealerId ? <Link to={`/admin/dealers/${row.dealerId}`}>Abrir</Link> : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </CityLayout>
  );
}

export function AdminExpansionCityInventoryPage() {
  const { citySlug } = useParams();
  const [data, setData] = useState(null);

  useEffect(() => {
    fetchCityInventoryReadiness(citySlug).then(setData).catch(() => {});
    trackCityExpansionEvent("city_inventory_readiness_viewed", { citySlug });
  }, [citySlug]);

  return (
    <CityLayout title="Inventory readiness" active="inventory">
      {data ? (
        <>
          <div className="dashboard-lead-stats">
            <span>Activos: {data.metrics?.activeListings}</span>
            <span>Clean: {data.cleanCount}</span>
            <span>Stale: {data.metrics?.staleListings}</span>
            <span>Sin fotos: {data.metrics?.missingPhotos}</span>
          </div>
          <h3>Coverage gaps</h3>
          <ul>{(data.coverageGaps || []).map((g) => <li key={g}>{g}</li>)}</ul>
          <Link to={`/admin/inventory-quality?city=${encodeURIComponent(data.config?.city || "")}`}>Abrir cola calidad →</Link>
        </>
      ) : (
        <p className="dealer-state-box">Cargando…</p>
      )}
    </CityLayout>
  );
}

export function AdminExpansionCitySeoPage() {
  const { citySlug } = useParams();
  const [pages, setPages] = useState([]);

  const load = () => fetchCitySeoReadiness(citySlug).then((payload) => setPages(payload.pages || []));

  useEffect(() => {
    load();
  }, [citySlug]);

  return (
    <CityLayout title="SEO readiness" active="seo">
      <button type="button" className="admin-btn admin-btn--primary" onClick={() => generateCitySeoConfigs(citySlug).then(load)}>
        Generar configs SEO
      </button>
      <div className="import-table-wrap">
        <table className="admin-table">
          <thead>
            <tr><th>Path</th><th>Tipo</th><th>Inventario</th><th>Index</th><th>Revisado</th></tr>
          </thead>
          <tbody>
            {pages.map((page) => (
              <tr key={page.id || page.path}>
                <td><a href={page.path} target="_blank" rel="noreferrer">{page.path}</a></td>
                <td>{page.pageType}</td>
                <td>{page.inventoryCount ?? 0}</td>
                <td>{page.noindex ? "noindex" : "index"}</td>
                <td>{page.reviewed ? "Sí" : "No"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </CityLayout>
  );
}

export function AdminExpansionCityCampaignsPage() {
  const { citySlug } = useParams();
  const [campaigns, setCampaigns] = useState([]);

  const load = () => fetchCityCampaigns(citySlug).then((payload) => setCampaigns(payload.campaigns || []));

  useEffect(() => {
    load();
  }, [citySlug]);

  const copyUrl = (url) => {
    navigator.clipboard?.writeText(url);
    trackCityExpansionEvent("city_campaign_url_copied", { citySlug });
  };

  return (
    <CityLayout title="Campaign readiness" active="campaigns">
      <button type="button" className="admin-btn admin-btn--primary" onClick={() => generateCityCampaigns(citySlug).then(load)}>
        Generar URLs campaña
      </button>
      <div className="import-table-wrap">
        <table className="admin-table">
          <thead>
            <tr><th>Campaña</th><th>Canal</th><th>Landing</th><th>Estado</th><th>UTM</th></tr>
          </thead>
          <tbody>
            {campaigns.map((c) => (
              <tr key={c.id}>
                <td>{c.campaignName}</td>
                <td>{c.channel}</td>
                <td>{c.targetLandingPage}</td>
                <td>{c.status}</td>
                <td><button type="button" onClick={() => copyUrl(c.utmUrl)}>Copiar URL</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </CityLayout>
  );
}

export function AdminExpansionCityPlacementsPage() {
  const { citySlug } = useParams();
  const [data, setData] = useState(null);

  useEffect(() => {
    fetchCityPlacements(citySlug).then(setData).catch(() => {});
    trackCityExpansionEvent("city_placement_readiness_viewed", { citySlug });
  }, [citySlug]);

  return (
    <CityLayout title="Placement readiness" active="placements">
      {data ? (
        <>
          <div className="dashboard-lead-stats">
            <span>Elegibles dealers: {data.eligibleDealers}</span>
            <span>Elegibles listings: {data.eligibleListings}</span>
            <span>Activos: {data.activePlacements?.length ?? 0}</span>
            <span>Programados: {data.scheduledPlacements?.length ?? 0}</span>
          </div>
          <Link to={`/admin/placements/listings?city=${encodeURIComponent(data.config?.city || "")}`}>Crear placement →</Link>
        </>
      ) : (
        <p className="dealer-state-box">Cargando…</p>
      )}
    </CityLayout>
  );
}

export function AdminExpansionCityAnalyticsPage() {
  const { citySlug } = useParams();
  const [data, setData] = useState(null);

  useEffect(() => {
    fetchCityExpansionAnalytics(citySlug).then(setData).catch(() => {});
    trackCityExpansionEvent("city_analytics_viewed", { citySlug });
  }, [citySlug]);

  const m = data?.marketplace;

  return (
    <CityLayout title="City analytics" active="analytics">
      {data ? (
        <div className="dashboard-lead-stats">
          <span>Sesiones: {m?.buyerSessions ?? 0}</span>
          <span>Búsquedas: {m?.searches ?? 0}</span>
          <span>Vistas: {m?.vehicleDetailViews ?? 0}</span>
          <span>WhatsApp: {m?.whatsappClicks ?? 0}</span>
          <span>Leads: {m?.leads ?? 0}</span>
          <span>Balance: {data.supplyDemandLabel || "—"}</span>
        </div>
      ) : (
        <p className="dealer-state-box">Cargando…</p>
      )}
    </CityLayout>
  );
}
